import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Button,
  Chip,
  IconButton,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Snackbar,
  Tooltip,
  Stack,
  Checkbox,
  Link,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import WarningIcon from "@mui/icons-material/Warning";
import InfoIcon from "@mui/icons-material/Info";
import GitHubIcon from "@mui/icons-material/GitHub";
import GroupWorkIcon from "@mui/icons-material/GroupWork";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import InputAdornment from "@mui/material/InputAdornment";
import { useNavigate } from "react-router-dom";
import { API_URL } from "../../lib/constants";
import { getValidAccessToken } from "../../lib/api/Auth";
import SystemErrorsChart, { SOURCE_DISPLAY_NAMES, getLastWeekDateString } from "./SystemErrorsChart";
import { setStoredLastSeenTimestamp } from "../../lib/hooks/useSystemErrorAlert";
import { useUserInfoContext } from "../../lib/contexts/UserInfoContext";

export interface ErrorLogItem {
  errorId: string;
  timestamp: string;
  environment: string;
  source: string;
  severity: string;
  message: string;
  stackTrace?: string;
  path?: string;
  userId?: string;
  userEmail?: string;
  chatId?: string;
  exchangeId?: string;
  resolved: boolean;
  resolvedAt?: string;
  resolvedBy?: string;
  resolutionComment?: string;
  status?: "unresolved" | "in_process" | "resolved" | string;
  groupId?: string;
  groupName?: string;
  githubIssueUrl?: string;
  githubIssueNumber?: number;
  githubIssueState?: string;
  githubPrUrl?: string;
  githubPrNumber?: number;
  metadata?: Record<string, any>;
}

function ErrorRow({
  row,
  selected,
  onToggleSelect,
  onOpenResolveModal,
  onViewConversation,
  onCopyMessageToSearch,
}: {
  row: ErrorLogItem;
  selected: boolean;
  onToggleSelect: (id: string) => void;
  onOpenResolveModal: (item: ErrorLogItem) => void;
  onViewConversation?: (chatId: string, userId?: string) => void;
  onCopyMessageToSearch?: (msg: string) => void;
}) {
  const [open, setOpen] = useState(false);

  const getSeverityChip = (severity: string) => {
    const s = (severity || "").toLowerCase();
    if (s === "fatal" || s === "error") {
      return <Chip icon={<ErrorOutlineIcon />} label={severity} color="error" size="small" variant="outlined" />;
    }
    if (s === "warn" || s === "warning") {
      return <Chip icon={<WarningIcon />} label={severity} color="warning" size="small" variant="outlined" />;
    }
    return <Chip icon={<InfoIcon />} label={severity} color="info" size="small" variant="outlined" />;
  };

  const getSourceChip = (source: string) => {
    const isFrontend = (source || "").toLowerCase().startsWith("frontend");
    const sourceLabel = (SOURCE_DISPLAY_NAMES && SOURCE_DISPLAY_NAMES[source]) || source;
    return (
      <Chip
        label={sourceLabel}
        size="small"
        sx={{
          backgroundColor: isFrontend ? "#e3f2fd" : "#f3e5f5",
          color: isFrontend ? "#0d47a1" : "#4a148c",
          fontWeight: "medium",
        }}
      />
    );
  };

  const getEnvChip = (env?: string) => {
    const safeEnv = env || "UNKNOWN";
    const e = safeEnv.toLowerCase();
    if (e === "prod") {
      return <Chip label="PROD" size="small" color="secondary" variant="filled" />;
    }
    if (e === "local") {
      return <Chip label="LOCAL" size="small" color="warning" variant="outlined" />;
    }
    if (e === "dev") {
      return <Chip label="DEV" size="small" color="primary" variant="outlined" />;
    }
    if (e === "unknown") {
      return <Chip label="UNKNOWN" size="small" variant="outlined" sx={{ color: "text.secondary", borderColor: "action.disabled" }} />;
    }
    return <Chip label={safeEnv.toUpperCase()} size="small" variant="outlined" />;
  };

  return (
    <>
      <TableRow sx={{ "& > *": { borderBottom: "unset" }, backgroundColor: row.resolved ? "#f8f9fa" : "inherit" }}>
        <TableCell padding="checkbox">
          <Checkbox
            checked={selected}
            onChange={() => onToggleSelect(row.errorId)}
            size="small"
          />
        </TableCell>
        <TableCell width="40px">
          <IconButton aria-label="expand row" size="small" onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell sx={{ fontSize: "0.85rem", whiteSpace: "nowrap" }}>
          {row.timestamp ? new Date(row.timestamp).toLocaleString() : "-"}
        </TableCell>
        <TableCell>{getEnvChip(row.environment)}</TableCell>
        <TableCell>{getSourceChip(row.source)}</TableCell>
        <TableCell>{getSeverityChip(row.severity)}</TableCell>
        <TableCell sx={{ maxWidth: "280px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Tooltip title={row.message}>
              <Typography variant="body2" noWrap fontWeight={row.resolved ? "normal" : "medium"} sx={{ flexGrow: 1, minWidth: 0 }}>
                {row.message}
              </Typography>
            </Tooltip>
            {onCopyMessageToSearch && (
              <Tooltip title="Search this error message">
                <IconButton
                  size="small"
                  aria-label="Search this error message"
                  onClick={(e) => {
                    e.stopPropagation();
                    onCopyMessageToSearch(row.message);
                  }}
                  sx={{ p: 0.3, flexShrink: 0 }}
                >
                  <SearchIcon sx={{ fontSize: "1rem" }} />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
          {row.groupName && (
            <Chip
              icon={<GroupWorkIcon />}
              label={row.groupName}
              size="small"
              color="primary"
              variant="outlined"
              sx={{ height: 20, fontSize: "0.7rem", mt: 0.5 }}
            />
          )}
        </TableCell>
        <TableCell sx={{ fontSize: "0.85rem" }}>
          {row.userEmail || row.userId || "-"}
        </TableCell>
        <TableCell align="center">
          <Stack direction="row" spacing={1} justifyContent="center" alignItems="center">
            {row.githubIssueUrl && (
              <Tooltip title={`View GitHub Issue (#${row.githubIssueNumber}${row.githubIssueState ? ` - ${row.githubIssueState}` : ""})`}>
                <Chip
                  icon={<GitHubIcon />}
                  label={`Issue #${row.githubIssueNumber}${row.githubIssueState ? ` (${row.githubIssueState})` : ""}`}
                  component="a"
                  href={row.githubIssueUrl}
                  target="_blank"
                  clickable
                  color={row.githubIssueState === "closed" ? "success" : "secondary"}
                  variant={row.githubIssueState === "closed" ? "outlined" : "filled"}
                  size="small"
                />
              </Tooltip>
            )}

            {row.githubPrUrl && (
              <Tooltip title={`View Resolution PR ${row.githubPrNumber ? `#${row.githubPrNumber}` : ""}`}>
                <Chip
                  icon={<GitHubIcon />}
                  label={`PR ${row.githubPrNumber ? `#${row.githubPrNumber}` : ""}`}
                  component="a"
                  href={row.githubPrUrl}
                  target="_blank"
                  clickable
                  color="success"
                  size="small"
                />
              </Tooltip>
            )}

            {row.resolved || row.status === "resolved" ? (
              <Chip
                icon={<CheckCircleIcon />}
                label="Resolved"
                color="success"
                size="small"
                onClick={() => onOpenResolveModal(row)}
                sx={{ cursor: "pointer" }}
              />
            ) : row.status === "in_process" ? (
              <Chip
                label="In Process"
                color="info"
                variant="filled"
                size="small"
                onClick={() => onOpenResolveModal(row)}
                sx={{ cursor: "pointer" }}
              />
            ) : (
              <Chip
                label="Unresolved"
                color="warning"
                variant="outlined"
                size="small"
                onClick={() => onOpenResolveModal(row)}
                sx={{ cursor: "pointer" }}
              />
            )}
          </Stack>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={9}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 2, p: 2, bgcolor: "#f5f5f5", borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom component="div" fontWeight="bold">
                Error Details
              </Typography>
              <GridDetails row={row} onViewConversation={onViewConversation} />
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

function CodeBlock({ text, label }: { text: string; label: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text)
        .then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        })
        .catch(() => {});
    }
  };

  return (
    <Box sx={{ position: "relative" }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
        <Tooltip title={copied ? "Copied!" : "Copy to clipboard"}>
          <IconButton size="small" onClick={handleCopy} sx={{ p: 0.2 }}>
            {copied ? <CheckIcon fontSize="small" color="success" /> : <ContentCopyIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      </Stack>
      <Box
        component="pre"
        sx={{
          p: 1.5,
          bgcolor: "#1e1e1e",
          color: "#f8f8f2",
          borderRadius: 1,
          whiteSpace: "pre-wrap",
          overflowWrap: "anywhere",
          wordBreak: "break-word",
          maxHeight: "250px",
          overflowX: "auto",
          overflowY: "auto",
          fontSize: "0.75rem",
          margin: 0,
        }}
      >
        {text}
      </Box>
    </Box>
  );
}

function GridDetails({
  row,
  onViewConversation,
}: {
  row: ErrorLogItem;
  onViewConversation?: (chatId: string, userId?: string) => void;
}) {
  return (
    <Stack spacing={1.5}>
      {row.message && (
        <Box>
          <Typography variant="caption" color="text.secondary" display="block">
            Error Message
          </Typography>
          <Typography variant="body2" sx={{ overflowWrap: "anywhere", wordBreak: "break-word", fontWeight: "medium" }}>
            {row.message}
          </Typography>
        </Box>
      )}

      <Box>
        <Typography variant="caption" color="text.secondary" display="block">
          Error ID / Path
        </Typography>
        <Typography variant="body2" fontFamily="monospace" sx={{ overflowWrap: "anywhere", wordBreak: "break-word" }}>
          {row.errorId} | {row.path || "N/A"}
        </Typography>
      </Box>

      {(row.chatId || row.exchangeId) && (
        <Box>
          <Typography variant="caption" color="text.secondary" display="block">
            Conversation / Exchange
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Typography variant="body2" fontFamily="monospace" sx={{ overflowWrap: "anywhere", wordBreak: "break-word" }}>
              {row.chatId && <><strong>Chat ID:</strong> {row.chatId}</>}
              {row.chatId && row.exchangeId && " | "}
              {row.exchangeId && <><strong>Exchange ID:</strong> {row.exchangeId}</>}
            </Typography>
            {row.chatId && onViewConversation && (
              <Button
                variant="outlined"
                size="small"
                onClick={() => onViewConversation(row.chatId!, row.userId)}
                sx={{ ml: 1, py: 0.2, px: 1, fontSize: "0.75rem" }}
              >
                View Transcript
              </Button>
            )}
          </Stack>
        </Box>
      )}

      {(row.metadata?.runner || row.metadata?.localUser) && (
        <Box>
          <Typography variant="caption" color="text.secondary" display="block">
            Local Runner
          </Typography>
          <Typography variant="body2" fontWeight="medium">
            {row.metadata?.runner || row.metadata?.localUser}
          </Typography>
        </Box>
      )}

      {row.groupId && (
        <Box>
          <Typography variant="caption" color="text.secondary" display="block">
            Related Group
          </Typography>
          <Typography variant="body2" sx={{ overflowWrap: "anywhere", wordBreak: "break-word" }}>
            <strong>Group Name:</strong> {row.groupName || row.groupId} (ID: <code>{row.groupId}</code>)
          </Typography>
        </Box>
      )}

      {row.githubIssueUrl && (
        <Alert severity="info" icon={<GitHubIcon />}>
          <Typography variant="body2" sx={{ overflowWrap: "anywhere", wordBreak: "break-word" }}>
            <strong>GitHub Issue:</strong>{" "}
            <Link href={row.githubIssueUrl} target="_blank" rel="noopener">
              Issue #{row.githubIssueNumber} ({row.githubIssueUrl})
            </Link>
          </Typography>
        </Alert>
      )}

      {row.githubPrUrl && (
        <Alert severity="success" icon={<GitHubIcon />}>
          <Typography variant="body2" sx={{ overflowWrap: "anywhere", wordBreak: "break-word" }}>
            <strong>Resolution PR:</strong>{" "}
            <Link href={row.githubPrUrl} target="_blank" rel="noopener">
              {row.githubPrNumber ? `PR #${row.githubPrNumber}` : row.githubPrUrl} ({row.githubPrUrl})
            </Link>
          </Typography>
        </Alert>
      )}

      {row.resolved && (
        <Alert severity="success" icon={<CheckCircleIcon />}>
          <Typography variant="body2" sx={{ overflowWrap: "anywhere", wordBreak: "break-word" }}>
            <strong>Resolved by:</strong> {row.resolvedBy || "Admin"}{" "}
            {row.resolvedAt ? `at ${new Date(row.resolvedAt).toLocaleString()}` : ""}
          </Typography>
          {row.resolutionComment && (
            <Typography variant="body2" sx={{ mt: 0.5, overflowWrap: "anywhere", wordBreak: "break-word" }}>
              <strong>Comment:</strong> {row.resolutionComment}
            </Typography>
          )}
        </Alert>
      )}

      {row.stackTrace && <CodeBlock text={row.stackTrace} label="Stack Trace" />}

      {row.metadata && Object.keys(row.metadata).length > 0 && (
        <CodeBlock text={JSON.stringify(row.metadata, null, 2)} label="Metadata JSON" />
      )}
    </Stack>
  );
}

const getTodayDateString = (): string => {
  const d = new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export default function ErrorsTab() {
  const navigate = useNavigate();
  let userId: string | undefined;
  let userInfo: any;
  try {
    const userContext = useUserInfoContext();
    userInfo = userContext?.userInfo;
    userId = userInfo?.userId || userInfo?.email;
  } catch {
    // Graceful fallback if rendered outside UserInfoContextProvider (e.g. isolated test environments)
  }
  const activeRequestIdRef = React.useRef<number>(0);
  const abortControllerRef = React.useRef<AbortController | null>(null);

  const [environment, setEnvironment] = useState<string>("all");
  const [source, setSource] = useState<string>("all");
  const [severity, setSeverity] = useState<string>("all");
  const [resolvedFilter, setResolvedFilter] = useState<string>("false");
  const [groupIdFilter, setGroupIdFilter] = useState<string>("");
  const [search, setSearch] = useState<string>("");

  const [debouncedSearch, setDebouncedSearch] = useState<string>("");
  const [debouncedGroupIdFilter, setDebouncedGroupIdFilter] = useState<string>("");

  const [startDate, setStartDate] = useState<string>(getLastWeekDateString());
  const [endDate, setEndDate] = useState<string>(getTodayDateString());

  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(0);
      setDebouncedSearch(search);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(0);
      setDebouncedGroupIdFilter(groupIdFilter);
    }, 300);
    return () => clearTimeout(timer);
  }, [groupIdFilter]);

  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(25);

  const [items, setItems] = useState<ErrorLogItem[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const [selectedErrorIds, setSelectedErrorIds] = useState<Set<string>>(new Set());
  const [creatingBulkIssue, setCreatingBulkIssue] = useState<boolean>(false);

  const [groupModalOpen, setGroupModalOpen] = useState<boolean>(false);
  const [groupNameInput, setGroupNameInput] = useState<string>("");
  const [groupingProgress, setGroupingProgress] = useState<boolean>(false);

  const [bulkResolveModalOpen, setBulkResolveModalOpen] = useState<boolean>(false);
  const [bulkResolveComment, setBulkResolveComment] = useState<string>("");
  const [bulkResolvePrInput, setBulkResolvePrInput] = useState<string>("");
  const [bulkResolveProgress, setBulkResolveProgress] = useState<boolean>(false);

  const [bulkUnresolveDialogOpen, setBulkUnresolveDialogOpen] = useState<boolean>(false);
  const [bulkUnresolveProgress, setBulkUnresolveProgress] = useState<boolean>(false);

  const [addIssueModalOpen, setAddIssueModalOpen] = useState<boolean>(false);
  const [addIssueInput, setAddIssueInput] = useState<string>("");
  const [addIssueProgress, setAddIssueProgress] = useState<boolean>(false);

  const [selectedError, setSelectedError] = useState<ErrorLogItem | null>(null);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [resolutionComment, setResolutionComment] = useState<string>("");
  const [resolutionPrInput, setResolutionPrInput] = useState<string>("");
  const [savingResolution, setSavingResolution] = useState<boolean>(false);

  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: "success" | "error" | "info" }>({
    open: false,
    message: "",
    severity: "info",
  });

  const showSnackbar = (message: string, severity: "success" | "error" | "info" = "info") => {
    setSnackbar({ open: true, message, severity });
  };

  const fetchErrors = useCallback(async () => {
    const requestId = ++activeRequestIdRef.current;
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    setLoading(true);
    setErrorMsg(null);
    try {
      const token = await getValidAccessToken(userInfo?.accessToken);
      if (!token) {
        if (requestId === activeRequestIdRef.current) {
          setErrorMsg("Authentication token unavailable. Please log in or refresh.");
          setLoading(false);
        }
        return;
      }
      const params = new URLSearchParams();
      if (environment !== "all") params.append("environment", environment);
      if (source !== "all") params.append("source", source);
      if (severity !== "all") params.append("severity", severity);
      if (resolvedFilter !== "all") params.append("resolved", resolvedFilter);
      if (debouncedGroupIdFilter.trim()) params.append("groupId", debouncedGroupIdFilter.trim());
      if (startDate) {
        const [sYear, sMonth, sDay] = startDate.split("-").map(Number);
        const startD = new Date(sYear, sMonth - 1, sDay, 0, 0, 0, 0);
        if (!isNaN(startD.getTime())) {
          params.append("startDate", startD.toISOString());
        }
      }
      if (endDate) {
        const [eYear, eMonth, eDay] = endDate.split("-").map(Number);
        const endD = new Date(eYear, eMonth - 1, eDay, 23, 59, 59, 999);
        if (!isNaN(endD.getTime())) {
          params.append("endDate", endD.toISOString());
        }
      }
      if (debouncedSearch.trim()) params.append("search", debouncedSearch.trim());

      params.append("limit", String(rowsPerPage));
      if (page > 0) {
        params.append("cursor", btoa(JSON.stringify({ index: page * rowsPerPage })));
      }

      const response = await fetch(`${API_URL}/admin/errors?${params.toString()}`, {
        signal: abortController.signal,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to load errors: ${response.statusText}`);
      }

      const data = await response.json();
      if (requestId === activeRequestIdRef.current) {
        const fetchedEntries = data.entries || [];
        setItems(fetchedEntries);
        setTotalCount(data.totalCount || 0);

        // Mark errors as seen upon visiting page 0 of unresolved errors without search/filtering restrictions or custom date ranges
        const isUnfilteredUnresolvedView =
          page === 0 &&
          !debouncedSearch.trim() &&
          environment === "all" &&
          source === "all" &&
          severity === "all" &&
          !debouncedGroupIdFilter &&
          startDate === getLastWeekDateString() &&
          resolvedFilter === "false";

        if (isUnfilteredUnresolvedView && fetchedEntries.length > 0) {
          const latest = fetchedEntries.find((e: ErrorLogItem) => !e.resolved && e.status !== "in_process");
          const ts = latest?.timestamp || (latest as any)?.createdAt || latest?.metadata?.timestamp;
          if (ts) {
            setStoredLastSeenTimestamp(ts, userId);
          }
        }
      }
    } catch (err: any) {
      if (err.name === "AbortError") return;
      if (requestId === activeRequestIdRef.current) {
        setErrorMsg(err.message || "An error occurred fetching errors log.");
      }
    } finally {
      if (requestId === activeRequestIdRef.current) {
        setLoading(false);
      }
    }
  }, [environment, source, severity, resolvedFilter, debouncedGroupIdFilter, startDate, endDate, debouncedSearch, page, rowsPerPage, userInfo?.accessToken]);

  const updateFilter = (setter: (val: any) => void) => (val: any) => {
    setPage(0);
    setter(val);
  };

  useEffect(() => {
    setSelectedErrorIds(new Set());
    fetchErrors();
  }, [fetchErrors]);

  const handleToggleSelectAll = () => {
    if (selectedErrorIds.size === items.length && items.length > 0) {
      setSelectedErrorIds(new Set());
    } else {
      setSelectedErrorIds(new Set(items.map((i) => i.errorId)));
    }
  };

  const handleToggleSelect = (id: string) => {
    const next = new Set(selectedErrorIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedErrorIds(next);
  };

  const getSelectedTimestampsMap = useCallback(() => {
    const map: Record<string, string> = {};
    for (const item of items) {
      if (selectedErrorIds.has(item.errorId)) {
        map[item.errorId] = item.timestamp;
      }
    }
    return map;
  }, [items, selectedErrorIds]);

  const handleGroupSelected = async () => {
    if (!selectedErrorIds.size) return;
    setGroupingProgress(true);
    try {
      const token = await getValidAccessToken(userInfo?.accessToken);
      const response = await fetch(`${API_URL}/admin/errors/group`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          errorIds: Array.from(selectedErrorIds),
          groupName: groupNameInput.trim() || undefined,
          timestamps: getSelectedTimestampsMap(),
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to group selected errors");
      }

      setGroupModalOpen(false);
      setGroupNameInput("");
      setSelectedErrorIds(new Set());
      showSnackbar("Errors grouped successfully", "success");
      fetchErrors();
    } catch (err: any) {
      showSnackbar(err.message || "Failed to group errors", "error");
    } finally {
      setGroupingProgress(false);
    }
  };

  const handleBulkResolve = async () => {
    if (!selectedErrorIds.size) return;
    setBulkResolveProgress(true);
    try {
      const token = await getValidAccessToken(userInfo?.accessToken);
      const response = await fetch(`${API_URL}/admin/errors/bulk-resolve`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          errorIds: Array.from(selectedErrorIds),
          resolved: true,
          comment: bulkResolveComment.trim() || undefined,
          githubPr: bulkResolvePrInput.trim() || undefined,
          timestamps: getSelectedTimestampsMap(),
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to resolve selected errors");
      }

      const resData = await response.json();
      setBulkResolveModalOpen(false);
      setBulkResolveComment("");
      setBulkResolvePrInput("");
      setSelectedErrorIds(new Set());
      showSnackbar(`Resolved ${resData.updatedCount ?? selectedErrorIds.size} errors!`, "success");
      fetchErrors();
    } catch (err: any) {
      showSnackbar(err.message || "Failed to resolve errors", "error");
    } finally {
      setBulkResolveProgress(false);
    }
  };

  const handleBulkUnresolve = async () => {
    if (!selectedErrorIds.size) return;
    setBulkUnresolveProgress(true);
    try {
      const token = await getValidAccessToken(userInfo?.accessToken);
      const response = await fetch(`${API_URL}/admin/errors/bulk-resolve`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          errorIds: Array.from(selectedErrorIds),
          resolved: false,
          timestamps: getSelectedTimestampsMap(),
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to unresolve selected errors");
      }

      const resData = await response.json();
      setBulkUnresolveDialogOpen(false);
      setSelectedErrorIds(new Set());
      showSnackbar(`Marked ${resData.updatedCount ?? selectedErrorIds.size} errors as unresolved!`, "success");
      fetchErrors();
    } catch (err: any) {
      showSnackbar(err.message || "Failed to unresolve errors", "error");
    } finally {
      setBulkUnresolveProgress(false);
    }
  };

  const handleAssociateIssue = async () => {
    if (!selectedErrorIds.size || !addIssueInput.trim()) return;
    setAddIssueProgress(true);
    try {
      const token = await getValidAccessToken(userInfo?.accessToken);
      const response = await fetch(`${API_URL}/admin/errors/associate-issue`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          errorIds: Array.from(selectedErrorIds),
          issue: addIssueInput.trim(),
          timestamps: getSelectedTimestampsMap(),
        }),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || "Failed to associate GitHub issue with selected errors");
      }

      const resData = await response.json();
      setAddIssueModalOpen(false);
      setAddIssueInput("");
      setSelectedErrorIds(new Set());
      showSnackbar(
        `Associated GitHub Issue #${resData.issueNumber} with ${resData.updatedCount ?? selectedErrorIds.size} error(s) and marked as In Process!`,
        "success"
      );
      fetchErrors();
    } catch (err: any) {
      showSnackbar(err.message || "Failed to associate GitHub issue", "error");
    } finally {
      setAddIssueProgress(false);
    }
  };

  const handleCreateBulkGithubIssue = async () => {
    if (!selectedErrorIds.size) return;
    setCreatingBulkIssue(true);
    try {
      const token = await getValidAccessToken(userInfo?.accessToken);
      const response = await fetch(`${API_URL}/admin/errors/bulk-github-issue`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          errorIds: Array.from(selectedErrorIds),
          timestamps: getSelectedTimestampsMap(),
        }),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || "Failed to create GitHub issue for selected errors");
      }

      const resData = await response.json();
      setSelectedErrorIds(new Set());
      showSnackbar(`Combined GitHub Issue #${resData.issueNumber} created for selected errors!`, "success");
      fetchErrors();
    } catch (err: any) {
      showSnackbar(err.message || "Error creating combined GitHub issue", "error");
    } finally {
      setCreatingBulkIssue(false);
    }
  };

  const handleOpenResolveModal = (item: ErrorLogItem) => {
    setSelectedError(item);
    setResolutionComment(item.resolutionComment || "");
    setResolutionPrInput(item.githubPrUrl || (item.githubPrNumber ? `#${item.githubPrNumber}` : ""));
    setModalOpen(true);
  };

  const handleCloseResolveModal = () => {
    setSelectedError(null);
    setResolutionComment("");
    setResolutionPrInput("");
    setModalOpen(false);
  };

  const handleViewConversation = (chatId: string, userId?: string) => {
    const params = new URLSearchParams();
    params.append("chatId", chatId);
    if (userId) params.append("user", userId);
    navigate(`/admin/transcripts?${params.toString()}`);
  };

  const handleToggleResolve = async (newResolvedState: boolean) => {
    if (!selectedError) return;
    setSavingResolution(true);
    try {
      const token = await getValidAccessToken(userInfo?.accessToken);
      const response = await fetch(`${API_URL}/admin/errors/${selectedError.errorId}/resolve`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          resolved: newResolvedState,
          comment: resolutionComment,
          githubPr: resolutionPrInput.trim(),
          timestamp: selectedError.timestamp,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to update resolution status");
      }

      handleCloseResolveModal();
      showSnackbar(newResolvedState ? "Error marked as resolved" : "Error marked as unresolved", "success");
      fetchErrors();
    } catch (err: any) {
      showSnackbar(err.message || "Error saving resolution", "error");
    } finally {
      setSavingResolution(false);
    }
  };

  return (
    <Box sx={{ p: 3, display: "flex", flexDirection: "column", height: "100%", overflowY: "auto" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Typography variant="h5" fontWeight="bold">
          System Errors Log
        </Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={fetchErrors}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {/* Errors & Warnings Chart Card */}
      <SystemErrorsChart
        environment={environment}
        source={source}
        severity={severity}
        resolvedFilter={resolvedFilter}
      />

      {/* Sticky Filters & Action Bar Container */}
      <Box sx={{ position: "sticky", top: 0, zIndex: 10, bgcolor: "background.default", pt: 1, pb: 2 }}>
        {/* Batch Action Toolbar when errors are selected */}
        {selectedErrorIds.size > 0 && (
          <Paper
            elevation={3}
            role="region"
            aria-label="Selected errors batch actions"
            sx={{
              p: 1.5,
              mb: 2,
              bgcolor: "action.hover",
              border: "1px solid",
              borderColor: "primary.main",
              borderRadius: 1,
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
              <Typography variant="subtitle2" color="primary" fontWeight="bold" sx={{ mr: 1 }}>
                {selectedErrorIds.size} error{selectedErrorIds.size > 1 ? "s" : ""} selected
              </Typography>
              <Button
                variant="contained"
                color="primary"
                size="small"
                startIcon={<GroupWorkIcon />}
                onClick={() => setGroupModalOpen(true)}
              >
                Group Selected
              </Button>
              <Button
                variant="contained"
                color="success"
                size="small"
                startIcon={<CheckCircleIcon />}
                onClick={() => setBulkResolveModalOpen(true)}
              >
                Resolve Selected
              </Button>
              <Button
                variant="contained"
                color="info"
                size="small"
                startIcon={<GitHubIcon />}
                onClick={() => setAddIssueModalOpen(true)}
              >
                Add Issue to Selected
              </Button>
              <Button
                variant="outlined"
                color="warning"
                size="small"
                onClick={() => setBulkUnresolveDialogOpen(true)}
              >
                Unresolve Selected
              </Button>
              <Button
                variant="contained"
                color="secondary"
                size="small"
                startIcon={creatingBulkIssue ? <CircularProgress size={14} color="inherit" /> : <GitHubIcon />}
                disabled={creatingBulkIssue}
                onClick={handleCreateBulkGithubIssue}
              >
                {selectedErrorIds.size === 1 ? "Create GitHub Issue" : "Create Combined GitHub Issue"}
              </Button>
            </Stack>
          </Paper>
        )}

        {/* Filters Bar */}
        <Paper sx={{ p: 2 }}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel>Environment</InputLabel>
              <Select
                value={environment}
                label="Environment"
                onChange={(e) => updateFilter(setEnvironment)(e.target.value)}
              >
                <MenuItem value="all">All Environments</MenuItem>
                <MenuItem value="local">Local</MenuItem>
                <MenuItem value="dev">Dev</MenuItem>
                <MenuItem value="prod">Prod</MenuItem>
                <MenuItem value="unknown">Unknown</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Source</InputLabel>
              <Select value={source} label="Source" onChange={(e) => updateFilter(setSource)(e.target.value)}>
                <MenuItem value="all">All Sources</MenuItem>
                <MenuItem value="frontend">Frontend (Main)</MenuItem>
                <MenuItem value="frontend-prince">Frontend (Prince)</MenuItem>
                <MenuItem value="frontend-lincoln">Frontend (Lincoln)</MenuItem>
                <MenuItem value="backend">Backend</MenuItem>
                {Array.from(new Set(items.map((i) => i.source).filter((s) => s && !["frontend", "frontend-prince", "frontend-lincoln", "backend"].includes(s)))).map((customSource) => (
                  <MenuItem key={customSource} value={customSource}>
                    {customSource}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Severity</InputLabel>
              <Select value={severity} label="Severity" onChange={(e) => updateFilter(setSeverity)(e.target.value)}>
                <MenuItem value="all">All Severities</MenuItem>
                <MenuItem value="error">Error</MenuItem>
                <MenuItem value="warn">Warn</MenuItem>
                <MenuItem value="fatal">Fatal</MenuItem>
                <MenuItem value="info">Info</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={resolvedFilter}
                label="Status"
                onChange={(e) => updateFilter(setResolvedFilter)(e.target.value)}
              >
                <MenuItem value="all">All Statuses</MenuItem>
                <MenuItem value="false">Unresolved</MenuItem>
                <MenuItem value="in_process">In Process</MenuItem>
                <MenuItem value="true">Resolved</MenuItem>
              </Select>
            </FormControl>

            <TextField
              size="small"
              label="Group ID / Name"
              placeholder="Filter by group..."
              value={groupIdFilter}
              onChange={(e) => setGroupIdFilter(e.target.value)}
              sx={{ minWidth: 160 }}
            />

            <TextField
              size="small"
              label="Search"
              placeholder="Message, user, error ID..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ minWidth: 220, flexGrow: 1 }}
              InputProps={{
                endAdornment: search ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      aria-label="clear search"
                      onClick={() => setSearch("")}
                      edge="end"
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />
          </Stack>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              size="small"
              label="Start Date"
              type="date"
              value={startDate}
              onChange={(e) => updateFilter(setStartDate)(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              size="small"
              label="End Date"
              type="date"
              value={endDate}
              onChange={(e) => updateFilter(setEndDate)(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Stack>
        </Paper>
      </Box>

      {errorMsg && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorMsg}
        </Alert>
      )}

      {/* Errors Table */}
      <TableContainer component={Paper} sx={{ minHeight: 400, flexShrink: 0, mb: 3 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={selectedErrorIds.size > 0 && selectedErrorIds.size < items.length}
                  checked={items.length > 0 && selectedErrorIds.size === items.length}
                  onChange={handleToggleSelectAll}
                  size="small"
                />
              </TableCell>
              <TableCell width="40px" />
              <TableCell>Timestamp</TableCell>
              <TableCell>Env</TableCell>
              <TableCell>Source</TableCell>
              <TableCell>Severity</TableCell>
              <TableCell>Message / Group</TableCell>
              <TableCell>User</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 5 }}>
                  <CircularProgress size={32} />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Loading recent errors...
                  </Typography>
                </TableCell>
              </TableRow>
            ) : items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 5 }}>
                  <Typography variant="body1" color="text.secondary">
                    No errors match the current filter criteria.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              items.map((row) => (
                <ErrorRow
                  key={row.errorId}
                  row={row}
                  selected={selectedErrorIds.has(row.errorId)}
                  onToggleSelect={handleToggleSelect}
                  onOpenResolveModal={handleOpenResolveModal}
                  onViewConversation={handleViewConversation}
                  onCopyMessageToSearch={(msg) => {
                    if (navigator.clipboard?.writeText) {
                      navigator.clipboard.writeText(msg).catch(() => {});
                    }
                    setSearch(msg);
                    showSnackbar("Search filter updated with error message", "info");
                  }}
                />
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </TableContainer>

      {/* Group Selected Errors Modal */}
      <Dialog open={groupModalOpen} onClose={() => setGroupModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Mark Selected Errors as Related</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Grouping <strong>{selectedErrorIds.size}</strong> selected error logs together.
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Group Name (e.g. 'Auth Timeout Issues', 'Vector Store Connection Drop')"
            type="text"
            fullWidth
            variant="outlined"
            value={groupNameInput}
            onChange={(e) => setGroupNameInput(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button onClick={() => setGroupModalOpen(false)} disabled={groupingProgress}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleGroupSelected}
            disabled={groupingProgress}
          >
            {groupingProgress ? "Grouping..." : "Group Errors"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Resolve Modal */}
      <Dialog open={bulkResolveModalOpen} onClose={() => setBulkResolveModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Resolve Selected Errors</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Marking <strong>{selectedErrorIds.size}</strong> selected errors as resolved.
          </Typography>
          <TextField
            margin="dense"
            label="Resolution GitHub PR URL or Number (e.g. '123' or 'https://github.com/.../pull/123')"
            type="text"
            fullWidth
            variant="outlined"
            value={bulkResolvePrInput}
            onChange={(e) => setBulkResolvePrInput(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            autoFocus
            margin="dense"
            label="Shared Resolution Comment / Notes (Optional)"
            type="text"
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            value={bulkResolveComment}
            onChange={(e) => setBulkResolveComment(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button onClick={() => setBulkResolveModalOpen(false)} disabled={bulkResolveProgress}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleBulkResolve}
            disabled={bulkResolveProgress}
          >
            {bulkResolveProgress ? "Resolving..." : "Resolve All Selected"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Issue to Selected Dialog */}
      <Dialog open={addIssueModalOpen} onClose={() => setAddIssueModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add GitHub Issue to Selected Errors</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter an existing GitHub issue URL or number to associate with <strong>{selectedErrorIds.size}</strong> selected error(s).
            The selected errors will be marked as <strong>In Process</strong> and grouped under this issue.
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="GitHub Issue URL or Number (e.g. '123', '#123', or 'https://github.com/.../issues/123')"
            type="text"
            fullWidth
            variant="outlined"
            value={addIssueInput}
            onChange={(e) => setAddIssueInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && addIssueInput.trim() && !addIssueProgress) {
                e.preventDefault();
                handleAssociateIssue();
              }
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button onClick={() => setAddIssueModalOpen(false)} disabled={addIssueProgress}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="info"
            onClick={handleAssociateIssue}
            disabled={addIssueProgress || !addIssueInput.trim()}
          >
            {addIssueProgress ? "Associating..." : "Associate Issue"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Unresolve Confirmation Dialog */}
      <Dialog open={bulkUnresolveDialogOpen} onClose={() => setBulkUnresolveDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Confirm Unresolve Selected</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Are you sure you want to mark <strong>{selectedErrorIds.size}</strong> selected error(s) as <strong>Unresolved</strong>?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button onClick={() => setBulkUnresolveDialogOpen(false)} disabled={bulkUnresolveProgress}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleBulkUnresolve}
            disabled={bulkUnresolveProgress}
          >
            {bulkUnresolveProgress ? "Updating..." : "Confirm Unresolve"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Single Item Resolve / Edit Resolution Dialog */}
      <Dialog open={modalOpen} onClose={handleCloseResolveModal} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedError?.resolved ? "Update Resolution Details" : "Mark Error as Resolved"}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            <strong>Error:</strong> {selectedError?.message}
          </Typography>
          <TextField
            margin="dense"
            label="Resolution GitHub PR URL or Number (e.g. '123' or 'https://github.com/.../pull/123')"
            type="text"
            fullWidth
            variant="outlined"
            value={resolutionPrInput}
            onChange={(e) => setResolutionPrInput(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            autoFocus
            margin="dense"
            label="Resolution Comment / Notes (Optional)"
            type="text"
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            value={resolutionComment}
            onChange={(e) => setResolutionComment(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          {(selectedError?.resolved || selectedError?.status === "in_process") && (
            <Button
              color="error"
              onClick={() => handleToggleResolve(false)}
              disabled={savingResolution}
            >
              Mark as Unresolved
            </Button>
          )}
          <Box sx={{ flexGrow: 1 }} />
          <Button onClick={handleCloseResolveModal} disabled={savingResolution}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={() => handleToggleResolve(true)}
            disabled={savingResolution}
          >
            {savingResolution ? "Saving..." : selectedError?.resolved ? "Save Changes" : "Resolve"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
